import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, FormControl, InputLabel, Select, MenuItem,
    Alert, CircularProgress, TextField, InputAdornment, FormControlLabel, Checkbox,
    Typography
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { useSelector } from 'react-redux';
import { FinanceService } from '../api/services/financeService';
import { SettingsService } from '../api/services/settingsService';
import { useCurrency, formatCurrency } from '../utils/currency';

export default function ProcessRefundModal({ open, onClose, reservation, tenantId, onSuccess, setNotification }) {
    const { currency, currencySymbol } = useCurrency();
    const { user } = useSelector((state) => state.auth || {});
    const activeTenantId = user?.tenantId;

    const [refundMethod, setRefundMethod] = useState('');
    const [amount, setAmount] = useState('');
    const [sendEmail, setSendEmail] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);

    // Calculate maximum allowable refund based on available balance fields
    const maxRefund = reservation ? (reservation.paidAmount || reservation.amount || reservation.totalAmount || 0) : 0;

    const fetchPaymentMethods = async () => {
        setLoadingMethods(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const resolvedTenantId = tenantId || activeTenantId || authData.tenantId || null;
            const res = await SettingsService.getPaymentMethods(resolvedTenantId, { noPagination: true });
            const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            const activeList = list.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');
            setPaymentMethods(activeList);
        } catch (err) {
            console.error('Failed to load refund payment methods from API:', err);
            setPaymentMethods([]);
        } finally {
            setLoadingMethods(false);
        }
    };

    useEffect(() => {
        if (open && reservation) {
            setRefundMethod('');
            setAmount(maxRefund > 0 ? maxRefund.toString() : '');
            setSendEmail(true);
            setError(null);
            fetchPaymentMethods();
        }
    }, [open, reservation, maxRefund]);

    const handleSave = async () => {
        const numAmount = parseFloat(amount);
        
        // Validation
        if (!refundMethod) {
            setError('Please select a refund method.');
            return;
        }
        if (isNaN(numAmount) || numAmount <= 0) {
            setError('Refund amount must be greater than 0.');
            return;
        }
        if (numAmount > maxRefund) {
            setError(`Refund amount cannot exceed the total amount paid (${formatCurrency(maxRefund, currency)}).`);
            return;
        }

        setIsProcessing(true);
        setError(null);
        try {
            const folioId = reservation.folio?.id || reservation.folio?.folioId || reservation.folioId || reservation.id;
            await FinanceService.processFolioRefund(folioId, {
                refundMethod,
                amount: numAmount,
                reason: 'Process Refund via UI',
                sendEmail: sendEmail,
                date: new Date().toISOString()
            });
            if (setNotification) {
                setNotification({ open: true, message: 'Refund transaction successfully registered and processed', severity: 'success' });
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to process refund';
            if (setNotification) {
                setNotification({ open: true, message: msg, severity: 'error' });
            } else {
                setError(msg);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    if (!reservation) return null;

    return (
        <Dialog 
            open={open} 
            onClose={isProcessing ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <UndoIcon color="primary" /> Process Refund
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControl fullWidth size="small" disabled={isProcessing || loadingMethods}>
                        <InputLabel id="refund-method-label">Refund Method</InputLabel>
                        <Select
                            labelId="refund-method-label"
                            value={refundMethod}
                            label="Refund Method"
                            onChange={(e) => setRefundMethod(e.target.value)}
                            disabled={isProcessing || loadingMethods}
                        >
                            {loadingMethods ? (
                                <MenuItem disabled value="">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2">Loading payment methods...</Typography>
                                    </Box>
                                </MenuItem>
                            ) : paymentMethods.length > 0 ? (
                                paymentMethods.map((method, idx) => {
                                    const val = typeof method === 'string' ? method : (method.name || method.code || method.id);
                                    const label = typeof method === 'string' ? method : (method.label || method.name || method.code || val);
                                    return (
                                        <MenuItem key={method.id || method._id || val || idx} value={val}>
                                            {label}
                                        </MenuItem>
                                    );
                                })
                            ) : (
                                <MenuItem disabled value="">
                                    <em>No active payment methods found</em>
                                </MenuItem>
                            )}
                            {refundMethod && !paymentMethods.some(m => (typeof m === 'string' ? m : (m.name || m.code || m.id)) === refundMethod) && (
                                <MenuItem key={refundMethod} value={refundMethod}>
                                    {refundMethod}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        size="small"
                        label="Refund Amount"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={isProcessing}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                            inputProps: { min: 0, max: maxRefund, step: 0.01 }
                        }}
                        helperText={`Maximum allowable refund: ${formatCurrency(maxRefund, currency)}`}
                    />
                    
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={sendEmail} 
                                onChange={(e) => setSendEmail(e.target.checked)} 
                                disabled={isProcessing}
                                color="primary" 
                            />
                        }
                        label="Send Refund Receipt to Guest Email"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={isProcessing}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                    disabled={isProcessing || !amount || !refundMethod}
                >
                    {isProcessing ? <CircularProgress size={20} color="inherit" /> : 'Issue Refund'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
